import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Slider from "react-slick";
import moment from "moment";
import { ApiCall } from "../../../../helper/axios";
import { formatPrice, formatePriceFn } from "../../../../helper/commonFunction";
import { ChevronLeftIcon, ChevronRightIcon } from "../../../../helper/HwIcon";
import { toUrlVendor, fromUrlVendor } from "../../../../helper/vendorMask";

const cardStyle = {
  border: "1px solid #e6e8ee",
  borderRadius: 12,
  padding: 12,
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  background: "#fff",
  minHeight: 400,
};

const kindChip = {
  fontSize: 11,
  fontWeight: 600,
  padding: "1px 8px",
  borderRadius: 12,
  background: "#eef0f3",
  color: "#151515",
  textTransform: "capitalize",
};
const amenChip = {
  fontSize: 11,
  fontWeight: 500,
  padding: "2px 8px",
  borderRadius: 12,
  background: "#f4f5f7",
  color: "#151515",
};
const ratingBadge = {
  fontSize: 12,
  fontWeight: 700,
  padding: "1px 7px",
  borderRadius: 6,
  background: "#137333",
  color: "#fff",
};
const navBtn = {
  width: 34,
  height: 34,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#EBEFFF",
  color: "#3264FF",
  border: "none",
  borderRadius: "50%",
  cursor: "pointer",
};
const navBtnDisabled = {
  opacity: 0.4,
  cursor: "not-allowed",
};
const bookBtn = {
  flex: "none",
  border: "none",
  background: "#3264FF",
  color: "#fff",
  fontSize: 14,
  fontWeight: 700,
  padding: "8px 14px",
  borderRadius: 6,
  cursor: "pointer",
  whiteSpace: "nowrap",
};
const computeSlidesToShow = () => {
  if (typeof window === "undefined") return 4;
  const w = window.innerWidth;
  if (w <= 576) return 1;
  if (w <= 992) return 2;
  if (w <= 1300) return 3;
  return 4;
};

const sliderSettings = {
  dots: false,
  arrows: false,
  infinite: false,
  speed: 400,
  slidesToShow: 4,
  slidesToScroll: 1,
  responsive: [
    { breakpoint: 1300, settings: { slidesToShow: 3 } },
    { breakpoint: 992, settings: { slidesToShow: 2 } },
    { breakpoint: 576, settings: { slidesToShow: 1 } },
  ],
};

const Stars = ({ count = 0 }) => {
  const n = Math.max(0, Math.min(5, Math.round(Number(count) || 0)));
  if (!n) return null;
  return (
    <span style={{ color: "#f5a623", fontSize: 13, letterSpacing: 1 }}>
      {Array.from({ length: n }).map((_, i) => (
        <span key={i}>★</span>
      ))}
    </span>
  );
};

export const SimilarProperties = ({ hid, searchQuery }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  const { agentActiveCurrency, displayPrice } = useSelector((s) => s.userData);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(computeSlidesToShow());

  useEffect(() => {
    const onResize = () => setSlidesToShow(computeSlidesToShow());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const checkin = searchQuery?.checkinDate;
  const checkout = searchQuery?.checkoutDate;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!hid || !checkin || !checkout) return;
      try {
        setLoading(true);
        setAttempted(true);
        const sp = new URLSearchParams(location.search);
        const residence = sp.get("residence") || undefined;
        const regionId = sp.get("cid") || undefined;
        const currentVendor = fromUrlVendor(sp.get("vendor"));
        const res = await ApiCall("POST", "/hotels/similar", {
          hid,
          checkinDate: moment(checkin).format("YYYY-MM-DD"),
          checkoutDate: moment(checkout).format("YYYY-MM-DD"),
          roomInfo: searchQuery?.roomInfo,
          residence,
          regionId,
          vendor: currentVendor,
        });
        if (!cancelled && res?.status === 200 && res?.data?.statusCode === 200) {
          setItems(Array.isArray(res?.data?.data) ? res.data.data : []);
          setCurrentSlide(0);
        }
      } catch (e) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [hid, checkin, checkout]);

  const goToHotel = (h) => {
    if (!h?.id) return;
    const params = new URLSearchParams(location.search || "");
    if (h?.hid !== null && h?.hid !== undefined) params.set("hid", h.hid);
    params.set("vendor", toUrlVendor("RATEHAWK"));
    params.set("commonId", "");
    window.scrollTo(0, 0);
    navigate(`/hotels/details/${h.id}?${params.toString()}`);
  };

  if (!attempted && !loading) return null;
  if (!loading && items.length === 0) return null;

  const showNav = items.length > 1;
  const prevDisabled = currentSlide <= 0;
  const nextDisabled = currentSlide >= items.length - slidesToShow;

  return (
    <div className="hw-hotel--singleListSection mb-20" id="similar_properties">
      <style>{`
        .hw-similarProperties--slider .slick-list{margin:0 -8px !important;}
        .hw-similarProperties--slider .slick-slide > div{padding:0 8px !important;box-sizing:border-box;}
      `}</style>
      <div className="d-flex justify-between align-items-center mb-20" style={{ justifyContent: "space-between" }}>
        <h2 className="h4 mb-0">Similar Properties</h2>
        {!loading && showNav ? (
          <div className="d-flex hw-gapxy10">
            <button type="button" style={{ ...navBtn, ...(prevDisabled ? navBtnDisabled : {}) }} disabled={prevDisabled} onClick={() => sliderRef.current?.slickPrev()} aria-label="Previous">
              <ChevronLeftIcon />
            </button>
            <button type="button" style={{ ...navBtn, ...(nextDisabled ? navBtnDisabled : {}) }} disabled={nextDisabled} onClick={() => sliderRef.current?.slickNext()} aria-label="Next">
              <ChevronRightIcon />
            </button>
          </div>
        ) : null}
      </div>
      {loading ? (
        <div className="d-flex hw-gapxy10" style={{ overflow: "hidden" }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={{ flex: 1, height: 400, borderRadius: 8, background: "#e9edf3" }} />
          ))}
        </div>
      ) : (
        <div className="hw-similarProperties--slider">
          <Slider ref={sliderRef} {...sliderSettings} afterChange={(i) => setCurrentSlide(i)}>
            {items.map((h) => (
              <div key={h.hid}>
                <div style={cardStyle} onClick={() => goToHotel(h)}>
                  <div
                    style={{
                      height: 160,
                      borderRadius: 10,
                      overflow: "hidden",
                      background: "#eef0f3",
                      marginBottom: 10,
                      flex: "none",
                    }}
                  >
                    {h.image ? (
                      <img src={h.image} alt={h.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : null}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                    <Stars count={h.starRating} />
                    {h.kind ? <span style={kindChip}>{h.kind}</span> : null}
                  </div>
                  <div className="hw-fontBold hw-text16" style={{ color: "#151515", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {h.name}
                  </div>
                  <div className="hw-text14 my-8" style={{ color: "#151515", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {h.address}
                  </div>
                  {h.userRating ? (
                    <div style={{ fontSize: 12, marginBottom: 8 }}>
                      <span style={ratingBadge}>{Number(h.userRating).toFixed(1)}</span>
                      {h.userRatingCount ? (
                        <span style={{ color: "#151515", marginLeft: 6 }}>{h.userRatingCount} reviews</span>
                      ) : null}
                    </div>
                  ) : null}
                  {Array.isArray(h.amenities) && h.amenities.length ? (
                    <div className="d-flex flex-wrap" style={{ gap: 4, marginBottom: 10,marginTop:5, overflow: "hidden" }}>
                      {h.amenities.slice(0, 2).map((a, i) => (
                        <span key={i} style={amenChip}>
                          {a}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <div style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    {h.price != null ? (
                      <span className="hw-fontBold hw-text18" style={{ fontSize: 18 , color : "black" }}>
                        {formatePriceFn(agentActiveCurrency, formatPrice(h.price, true, true), displayPrice)}
                      </span>
                    ) : <span />}
                    <button
                      type="button"
                      style={bookBtn}
                      onClick={(e) => { e.stopPropagation(); goToHotel(h); }}
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      )}
    </div>
  );
};

export default SimilarProperties;
